import React, { useEffect, useMemo, useState } from 'react';
import createPlotlyComponent from 'react-plotly.js/factory';
import Plotly from 'plotly.js-basic-dist-min';
import { apiFetch } from '../constants';

const Plot = createPlotlyComponent(Plotly);

const PLOT_CONFIG = { displaylogo: false, responsive: true };
const PLOT_FONT = { family: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif', size: 12, color: '#1f2937' };

const fmt = (v, digits = 2) => (v == null || Number.isNaN(v) ? '—' : Number(v).toFixed(digits));

function KpiCard({ label, value, hint }) {
  return (
    <div style={{
      flex: '1 1 160px', minWidth: 160, padding: '12px 16px',
      background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
    }}>
      <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, color: '#0f172a', marginTop: 4 }}>{value}</div>
      {hint && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{hint}</div>}
    </div>
  );
}

export default function PowerFlowReports({ taskId, backendUrl }) {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({ kind: 'lv', trafo: '' });
  const [series, setSeries] = useState(null);
  const [seriesLoading, setSeriesLoading] = useState(false);

  // Fetch summary on mount / task change
  useEffect(() => {
    if (!taskId) return;
    setLoading(true);
    setError(null);
    const controller = new AbortController();
    apiFetch(`${backendUrl}/tasks/${encodeURIComponent(taskId)}/pf-summary`, { signal: controller.signal })
      .then(r => r.json())
      .then(data => {
        if (data.status !== 'ok') throw new Error(data.message || 'Failed to load PF summary');
        setSummary(data);
        // Auto-select the first LV grid for the drill-down
        if (data.lv && data.lv.length > 0) {
          setSelected({ kind: 'lv', trafo: data.lv[0].trafo });
        } else if (data.mv && data.mv.length > 0) {
          setSelected({ kind: 'mv', trafo: data.mv[0].trafo });
        }
      })
      .catch(err => {
        if (err.name !== 'AbortError') setError(err.message);
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [taskId, backendUrl]);

  // Fetch per-grid time series when selection changes
  useEffect(() => {
    if (!taskId || !selected.trafo) return;
    setSeriesLoading(true);
    setSeries(null);
    const controller = new AbortController();
    const url = `${backendUrl}/tasks/${encodeURIComponent(taskId)}/pf-series?kind=${selected.kind}&trafo=${encodeURIComponent(selected.trafo)}`;
    apiFetch(url, { signal: controller.signal })
      .then(r => r.json())
      .then(data => {
        if (data.status !== 'ok') throw new Error(data.message || 'Failed to load series');
        setSeries(data);
      })
      .catch(err => {
        if (err.name !== 'AbortError') console.error(err);
      })
      .finally(() => setSeriesLoading(false));
    return () => controller.abort();
  }, [taskId, selected, backendUrl]);

  // --- Plot 1: voltage compliance per LV grid (sorted ascending by v_min) ---
  const voltagePlot = useMemo(() => {
    if (!summary?.lv?.length) return null;
    const rows = summary.lv
      .filter(r => r.v_min_pu != null)
      .sort((a, b) => a.v_min_pu - b.v_min_pu);
    if (!rows.length) return null;
    const colors = rows.map(r =>
      r.v_min_pu < 0.90 ? '#dc2626' :          // statutory violation
      r.v_min_pu < 0.95 ? '#f59e0b' :          // warning (planning band)
      '#16a34a'                                 // OK
    );
    return {
      data: [{
        type: 'bar',
        x: rows.map(r => r.trafo),
        y: rows.map(r => r.v_min_pu),
        marker: { color: colors },
        text: rows.map(r => `${fmt(r.v_min_pu, 3)} pu`),
        hovertemplate: '<b>%{x}</b><br>v_min: %{y:.3f} pu<extra></extra>',
      }],
      layout: {
        title: { text: 'Worst LV bus voltage per grid (24 h)', font: { size: 14 } },
        xaxis: { title: '', tickangle: -45, automargin: true, showticklabels: rows.length <= 30 },
        yaxis: { title: 'v_min [pu]', range: [Math.min(0.88, ...rows.map(r => r.v_min_pu)) - 0.01, 1.05] },
        shapes: [
          { type: 'line', xref: 'paper', x0: 0, x1: 1, y0: 0.95, y1: 0.95, line: { color: '#f59e0b', dash: 'dot', width: 1 } },
          { type: 'line', xref: 'paper', x0: 0, x1: 1, y0: 0.90, y1: 0.90, line: { color: '#dc2626', dash: 'dash', width: 1 } },
        ],
        annotations: [
          { xref: 'paper', x: 1, y: 0.95, xanchor: 'right', yanchor: 'bottom', text: '0.95 (warning)', font: { size: 10, color: '#f59e0b' }, showarrow: false },
          { xref: 'paper', x: 1, y: 0.90, xanchor: 'right', yanchor: 'bottom', text: '0.90 (statutory)', font: { size: 10, color: '#dc2626' }, showarrow: false },
        ],
        margin: { t: 40, l: 50, r: 20, b: 60 },
        font: PLOT_FONT, paper_bgcolor: '#fff', plot_bgcolor: '#fff',
      },
    };
  }, [summary]);

  // --- Plot 2: trafo peak loading (LV + MV) sorted descending ---
  const loadingPlot = useMemo(() => {
    if (!summary) return null;
    const lv = (summary.lv || []).filter(r => r.peak_loading_percent != null);
    const mv = (summary.mv || []).filter(r => r.peak_loading_percent != null);
    if (!lv.length && !mv.length) return null;
    const colorFor = pct => pct > 100 ? '#dc2626' : pct > 80 ? '#f59e0b' : '#16a34a';
    const lvSorted = [...lv].sort((a, b) => b.peak_loading_percent - a.peak_loading_percent);
    const mvSorted = [...mv].sort((a, b) => b.peak_loading_percent - a.peak_loading_percent);
    const traces = [];
    if (mvSorted.length) traces.push({
      type: 'bar', name: 'MV substation',
      x: mvSorted.map(r => r.trafo), y: mvSorted.map(r => r.peak_loading_percent),
      marker: { color: mvSorted.map(r => colorFor(r.peak_loading_percent)) },
      hovertemplate: '<b>%{x}</b><br>peak loading: %{y:.1f}%<extra></extra>',
    });
    if (lvSorted.length) traces.push({
      type: 'bar', name: 'LV trafo',
      x: lvSorted.map(r => r.trafo), y: lvSorted.map(r => r.peak_loading_percent),
      marker: { color: lvSorted.map(r => colorFor(r.peak_loading_percent)) },
      hovertemplate: '<b>%{x}</b><br>peak loading: %{y:.1f}%<extra></extra>',
    });
    return {
      data: traces,
      layout: {
        title: { text: 'Peak transformer loading (24 h, sorted)', font: { size: 14 } },
        xaxis: { title: '', tickangle: -45, automargin: true, showticklabels: (lv.length + mv.length) <= 30 },
        yaxis: { title: 'Peak loading [%]' },
        shapes: [
          { type: 'line', xref: 'paper', x0: 0, x1: 1, y0: 100, y1: 100, line: { color: '#dc2626', dash: 'dash', width: 1 } },
          { type: 'line', xref: 'paper', x0: 0, x1: 1, y0: 80,  y1: 80,  line: { color: '#f59e0b', dash: 'dot',  width: 1 } },
        ],
        barmode: 'group',
        showlegend: traces.length > 1,
        margin: { t: 40, l: 50, r: 20, b: 60 },
        font: PLOT_FONT, paper_bgcolor: '#fff', plot_bgcolor: '#fff',
      },
    };
  }, [summary]);

  // --- Plot 3: per-grid 24-h profile with voltage band ---
  const seriesPlot = useMemo(() => {
    if (!series || !series.time) return null;
    const x = series.time;
    const traces = [];
    if (series.p_mv_mw) traces.push({
      type: 'scatter', mode: 'lines', name: 'P at trafo (MW)',
      x, y: series.p_mv_mw, line: { color: '#2563eb', width: 2 },
      yaxis: 'y',
    });
    if (series.q_mv_mvar) traces.push({
      type: 'scatter', mode: 'lines', name: 'Q at trafo (MVAr)',
      x, y: series.q_mv_mvar, line: { color: '#7c3aed', width: 1, dash: 'dot' },
      yaxis: 'y',
    });
    if (series.loading_percent) traces.push({
      type: 'scatter', mode: 'lines', name: 'Loading (%)',
      x, y: series.loading_percent, line: { color: '#f97316', width: 2 },
      yaxis: 'y2',
    });
    // Voltage band — only present for LV
    if (series.v_min_pu && series.v_max_pu) {
      traces.push({
        type: 'scatter', mode: 'lines', name: 'v_max', x, y: series.v_max_pu,
        line: { color: '#cbd5e1', width: 0 },
        yaxis: 'y3', showlegend: false, hoverinfo: 'skip',
      });
      traces.push({
        type: 'scatter', mode: 'lines', name: 'voltage band',
        x, y: series.v_min_pu,
        line: { color: '#cbd5e1', width: 0 },
        fill: 'tonexty', fillcolor: 'rgba(203,213,225,0.55)',
        yaxis: 'y3', hoverinfo: 'skip',
      });
      if (series.v_mean_pu) traces.push({
        type: 'scatter', mode: 'lines', name: 'v_mean (pu)',
        x, y: series.v_mean_pu, line: { color: '#0ea5e9', width: 2 },
        yaxis: 'y3',
      });
    }

    return {
      data: traces,
      layout: {
        title: { text: `24 h profile — ${selected.trafo}`, font: { size: 14 } },
        xaxis: { title: 'time', automargin: true },
        // When there's no voltage subplot (MV grids), let power/loading use the
        // full plot height — otherwise the yearly curve is squeezed into the top
        // 45% and the bottom half renders blank.
        yaxis:  { title: 'Power [MW / MVAr]', domain: series.v_min_pu ? [0.55, 1.0] : [0.0, 1.0] },
        yaxis2: { title: 'Loading [%]', overlaying: 'y', side: 'right', domain: series.v_min_pu ? [0.55, 1.0] : [0.0, 1.0], range: [0, 130] },
        yaxis3: series.v_min_pu ? { title: 'Voltage [pu]', domain: [0.0, 0.45], range: [0.88, 1.10] } : undefined,
        grid: series.v_min_pu ? { rows: 2, columns: 1, pattern: 'independent' } : undefined,
        shapes: series.v_min_pu ? [
          { type: 'line', xref: 'paper', x0: 0, x1: 1, y0: 0.95, y1: 0.95, yref: 'y3', line: { color: '#f59e0b', dash: 'dot', width: 1 } },
          { type: 'line', xref: 'paper', x0: 0, x1: 1, y0: 1.05, y1: 1.05, yref: 'y3', line: { color: '#f59e0b', dash: 'dot', width: 1 } },
          { type: 'line', xref: 'paper', x0: 0, x1: 1, y0: 100,  y1: 100,  yref: 'y2', line: { color: '#dc2626', dash: 'dash', width: 1 } },
        ] : [
          { type: 'line', xref: 'paper', x0: 0, x1: 1, y0: 100,  y1: 100,  yref: 'y2', line: { color: '#dc2626', dash: 'dash', width: 1 } },
        ],
        legend: { orientation: 'h', y: 1.07, x: 0 },
        margin: { t: 50, l: 50, r: 50, b: 50 },
        font: PLOT_FONT, paper_bgcolor: '#fff', plot_bgcolor: '#fff',
      },
    };
  }, [series, selected.trafo]);

  if (loading) {
    return <div style={{ padding: 24, color: '#475569' }}>Loading power flow results…</div>;
  }
  if (error) {
    return <div style={{ padding: 24, color: '#dc2626' }}>Could not load PF results: {error}</div>;
  }
  if (!summary || ((!summary.lv?.length) && (!summary.mv?.length))) {
    return (
      <div style={{ padding: 24, color: '#475569' }}>
        No power flow results available. Run "Run Power Flows" from the menu first.
      </div>
    );
  }

  const sys = summary.system || {};
  const lvOptions = summary.lv || [];
  const mvOptions = summary.mv || [];

  return (
    // Left padding clears the floating LayerPanel (top:16, left:16, width:236
    // plus 32 px panel padding ≈ 284 px); add a safety margin to 300 px.
    <div style={{
      padding: '20px 20px 20px 300px', height: '100%', overflowY: 'auto',
      background: '#fff', color: '#0f172a',
    }}>
      <h2 style={{ margin: '0 0 16px 0', fontSize: 18 }}>Power Flow Results</h2>

      {/* System KPIs */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
        <KpiCard label="LV grids"               value={sys.n_lv_grids ?? '—'} />
        <KpiCard label="MV substations"         value={sys.n_mv_grids ?? '—'} />
        <KpiCard label="LV grids w/ violations" value={sys.n_violating_lv ?? '—'}
                  hint={sys.n_lv_grids ? `${((sys.n_violating_lv || 0) * 100 / sys.n_lv_grids).toFixed(0)}% of LV grids` : ''} />
        <KpiCard label="MV energy supplied"     value={`${fmt(sys.total_supplied_mv_mwh, 1)} MWh`} hint="24 h aggregate" />
        <KpiCard label="LV load served"         value={`${fmt(sys.total_lv_load_mwh, 1)} MWh`} hint="24 h aggregate" />
        <KpiCard label="System losses"
                  value={sys.losses_mwh != null ? `${fmt(sys.losses_mwh, 2)} MWh` : '—'}
                  hint={sys.loss_pct != null ? `${fmt(sys.loss_pct, 1)} % of supplied` : ''} />
      </div>

      {/* Plot 1 — voltage compliance */}
      {voltagePlot && (
        <div style={{ marginBottom: 24 }}>
          <Plot data={voltagePlot.data} layout={voltagePlot.layout}
                config={PLOT_CONFIG} style={{ width: '100%', height: 360 }} useResizeHandler />
        </div>
      )}

      {/* Plot 2 — trafo loading ranking */}
      {loadingPlot && (
        <div style={{ marginBottom: 24 }}>
          <Plot data={loadingPlot.data} layout={loadingPlot.layout}
                config={PLOT_CONFIG} style={{ width: '100%', height: 360 }} useResizeHandler />
        </div>
      )}

      {/* Plot 3 — per-grid drill-down */}
      <div style={{
        padding: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 500 }}>Drill-down:</span>
          <select
            value={selected.kind}
            onChange={e => {
              const kind = e.target.value;
              const list = kind === 'lv' ? lvOptions : mvOptions;
              setSelected({ kind, trafo: list[0]?.trafo || '' });
            }}
            style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #cbd5e1' }}
          >
            <option value="lv" disabled={lvOptions.length === 0}>LV grid</option>
            <option value="mv" disabled={mvOptions.length === 0}>MV substation</option>
          </select>
          <select
            value={selected.trafo}
            onChange={e => setSelected(prev => ({ ...prev, trafo: e.target.value }))}
            style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #cbd5e1', minWidth: 180 }}
          >
            {(selected.kind === 'lv' ? lvOptions : mvOptions).map(r => (
              <option key={r.trafo} value={r.trafo}>{r.trafo}</option>
            ))}
          </select>
          {seriesLoading && <span style={{ color: '#64748b', fontSize: 12 }}>Loading…</span>}
        </div>
        {seriesPlot ? (
          <Plot data={seriesPlot.data} layout={seriesPlot.layout}
                config={PLOT_CONFIG}
                style={{ width: '100%', height: series?.v_min_pu ? 520 : 360 }}
                useResizeHandler />
        ) : (
          !seriesLoading && <div style={{ padding: 24, color: '#64748b', fontSize: 13 }}>
            Select a grid to see its 24 h profile.
          </div>
        )}
      </div>
    </div>
  );
}
